import React from 'react';
import Link from 'next/link';
import { Star } from 'lucide-react';
import { ExportSquare, Message } from 'iconsax-react';
import { BlueCheck, GoldCheck, RankedBadge } from '@/components/Icons';
import { Exchange } from '@/types/exchanges/page';
import { getBadge } from '@/components/(postTypes)/platforms/(archive)/layout/ExchangesListCard';
import Image from 'next/image';

interface ExchangeChildItemsProps {
    childItem?: Exchange;
    index: number;
    rating: number;
    ads?: boolean;
}

const ExchangeListCard: React.FC<ExchangeChildItemsProps> = ({ childItem, index, ads, rating }) => {
    const { faName, enName, logo, ctaButton, ctaId, badge, sponsored, verified } =
        childItem?.brokerSingle?.newfields || {};
    const badgeContent = getBadge(index, false, '');
    return (
        <div
            className={`relative flex lg:pe-2 overflow-hidden border p-1 rounded-lg w-full dark:border-white/20`}
        >
            <div className={`w-16 flex justify-center text-2xl`}>
                <div className={'flex flex-col justify-center items-center gap-3'}>
                    {ads ? <RankedBadge className={'size-8'} /> : <>{badgeContent}</>}
                </div>
            </div>
            <div
                className={`w-full border-r border-primary/10 dark:border-white/20 pr-2 lg:pr-3 p-1 flex flex-col lg:flex-row justify-center lg:justify-between text-white lg:items-center gap-3`}
            >
                <Link
                    key={index}
                    href={childItem?.uri || '#'}
                    className={'flex items-center gap-2'}
                >
                    <Image
                        className={'size-8 rounded-none object-contain'}
                        src={logo?.mediaItemUrl || ''}
                        loading='lazy'
                        width={120}
                        height={120}
                        alt={enName || ''}
                    />

                    <div>
                        <div className={'flex flex-col items-start dark:text-white text-primary'}>
                            <div className={`inline-flex items-center gap-1`}>
                                <span className={'line-clamp-1'}>{faName}</span>
                                <span className={`text-xxs dark:text-white/80 text-primary/80`}>
                                    ({enName})
                                </span>
                                <div className={'w-4'}>
                                    {sponsored ? (
                                        <GoldCheck className={'w-full h-full'} />
                                    ) : verified ? (
                                        <BlueCheck className={'w-full h-full'} />
                                    ) : (
                                        <></>
                                    )}
                                </div>
                            </div>
                            {badge && (
                                <span className={'text-xs text-progress font-medium lg:hidden'}>
                                    {badge}
                                </span>
                            )}
                        </div>

                        <div className={`flex gap-3 justify-start group-hover:text-white mt-2`}>
                            {badge && (
                                <>
                                    <span
                                        className={
                                            'text-xs text-progress font-medium max-lg:hidden'
                                        }
                                    >
                                        {badge}
                                    </span>
                                    <span
                                        className={`text-xs font-medium text-primary/40 dark:text-white/40 max-lg:hidden`}
                                    >
                                        &#9679;
                                    </span>
                                </>
                            )}

                            <div
                                className={
                                    'inline-flex items-center gap-2 text-xs text-primary/60 dark:text-white/70'
                                }
                            >
                                <Message
                                    size='16'
                                    className={
                                        'leading-none stroke-primary/60 dark:stroke-white/60'
                                    }
                                />
                                <div>
                                    {childItem?.postRatings.count}
                                    <span className={'mr-1'}>نظر</span>
                                </div>
                            </div>
                            <span
                                className={'text-xs font-medium text-primary/40 dark:text-white/70'}
                            >
                                &#9679;
                            </span>
                            <div
                                className={
                                    'flex items-center gap-x-1 text-primary/60 dark:text-white/70'
                                }
                            >
                                <span>
                                    <Star
                                        className={`group-hover:fill-white dark:fill-white/40`}
                                        strokeWidth={1.5}
                                        size={12}
                                    />
                                </span>
                                <span className={'text-xs'}>{rating} از ۵ امتیاز</span>
                            </div>
                        </div>
                    </div>
                </Link>
                {childItem?.uri && (
                    <Link
                        href={ctaButton?.url || '#'}
                        target={'_blank'}
                        rel={'nofollow'}
                        id={ctaId || ''}
                        className={`bg-progress p-3 text-white whitespace-nowrap rounded-lg lg:rounded-xl text-sm transition-all hover:bg-transparent border border-progress hover:text-progress flex justify-center items-center gap-2 max-lg:px-2 max-lg:text-xs max-lg:w-full max-lg:text-center stroke-white hover:stroke-progress`}
                    >
                        <ExportSquare variant={'TwoTone'} size={16} />
                        <span className={'z-[1]'}>{ctaButton?.title || 'افتتاح حساب'}</span>
                    </Link>
                )}
            </div>
        </div>
    );
};

export default ExchangeListCard;
